import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import FacebookLoginButton from '../components/FacebookLoginButton';
import AppleLoginButton from '../components/AppleLoginButton';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Button from '../components/ui/Button';
import '../styles/Auth.css';

const Register = () => {
  // ============================================
  // HOOKS
  // ============================================
  const { t, language } = useI18n();
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const bgImage = theme === 'dark'
    ? "url('/la_marina_alta_midnight_blue.webp')"
    : "url('/la_marina_alta_ocean.webp')";
  const [searchParams] = useSearchParams();

  // Invitation token (URL-ből: /register?invite=TOKEN)
  const inviteToken = searchParams.get('invite');
  const [inviteBanner, setInviteBanner] = useState('');

  const [ssoConfig, setSsoConfig] = useState({ sso_google_enabled: false, sso_facebook_enabled: false, sso_apple_enabled: false });
  useEffect(() => {
    fetch('/api/auth/sso-config')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSsoConfig(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!inviteToken) return;
    fetch(`/api/auth/invitation?token=${inviteToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.email) {
          setFormData(prev => ({ ...prev, email: data.email }));
          setInviteBanner(`Meghívó: ${data.email} — Role: ${data.role === 0 ? 'Superadmin' : data.role === 1 ? 'Admin' : 'User'}`);
        }
      })
      .catch(() => {});
  }, [inviteToken]);

  // ============================================
  // STATE VÁLTOZÓK
  // ============================================
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // ============================================
  // INPUT CHANGE HANDLER
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // ============================================
  // FORM VALIDÁCIÓ (CLIENT-SIDE)
  // ============================================
  const validateForm = () => {
    const errors = {};

    // EMAIL VALIDÁCIÓ
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = t('auth.validation.invalidEmail');
    }

    // JELSZÓ VALIDÁCIÓ
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      errors.password = t('auth.validation.passwordTooWeak');
    }

    // JELSZÓ MEGERŐSÍTÉS
    if (formData.password !== confirmPassword) {
      errors.confirmPassword = t('auth.validation.passwordMismatch');
    }

    // USERNAME VALIDÁCIÓ
    if (!/^[a-zA-Z0-9_]{3,30}$/.test(formData.username)) {
      errors.username = t('auth.validation.invalidUsername');
    }

    // NAMES VALIDÁCIÓ
    if (formData.firstName && formData.firstName.length > 100) {
      errors.firstName = t('auth.validation.nameTooLong');
    }

    if (formData.lastName && formData.lastName.length > 100) {
      errors.lastName = t('auth.validation.nameTooLong');
    }

    return errors;
  };

  // ============================================
  // FORM SUBMIT HANDLER
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    const result = await register(
      formData.email,
      formData.username,
      formData.password,
      formData.firstName,
      formData.lastName,
      inviteToken || undefined,
      language
    );

    if (result.success) {
      navigate('/dashboard');
    }

    setLoading(false);
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="auth-container" style={{ backgroundImage: bgImage }}>
      {/* LANGUAGE SWITCHER */}
      <div className="language-switcher-top">
        <LanguageSwitcher />
      </div>

      <div className="auth-card">
        <div className="auth-card-header">
          <img src="/villa_presence_concierge_logo.svg" alt="" className="auth-logo-sm" aria-hidden="true" />
          <p className="auth-card-eyebrow">Client Portal</p>
          <h1>{t('auth.register')}</h1>
        </div>

        {/* INVITATION BANNER */}
        {inviteBanner && (
          <div className="invitation-info">
            📨 {inviteBanner}
          </div>
        )}

        {/* GLOBAL ERROR */}
        {error && (
          <div className="error-message">
            {t(`auth.errors.${error.replace(/\s+/g, '_').toLowerCase()}`, error)}
          </div>
        )}

        {/* REGISTER FORM */}
        <form onSubmit={handleSubmit} className="auth-form">
          
          {/* NÉV MEZŐK */}
          <div className="form-row">
            {/* FIRST NAME */}
            <div className="form-group">
              <label htmlFor="firstName">
                {t('auth.firstName')}
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
                placeholder={t('auth.firstName')}
              />
              {fieldErrors.firstName && (
                <span className="field-error">
                  {fieldErrors.firstName}
                </span>
              )}
            </div>

            {/* LAST NAME */}
            <div className="form-group">
              <label htmlFor="lastName">
                {t('auth.lastName')}
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
                placeholder={t('auth.lastName')}
              />
              {fieldErrors.lastName && (
                <span className="field-error">
                  {fieldErrors.lastName}
                </span>
              )}
            </div>
          </div>

          {/* EMAIL */}
          <div className="form-group">
            <label htmlFor="email">
              {t('auth.email')} *
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder={t('auth.email')}
            />
            {fieldErrors.email && (
              <span className="field-error">
                {fieldErrors.email}
              </span>
            )}
          </div>

          {/* USERNAME */}
          <div className="form-group">
            <label htmlFor="username">
              {t('auth.username')} *
            </label>
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder={t('auth.username')}
            />
            {fieldErrors.username && (
              <span className="field-error">
                {fieldErrors.username}
              </span>
            )}
          </div>

          {/* JELSZÓ */}
          <div className="form-group">
            <label htmlFor="password">
              {t('auth.password')} *
            </label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder={t('auth.password')}
            />
            {fieldErrors.password && (
              <span className="field-error">
                {fieldErrors.password}
              </span>
            )}
            <small style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
              {t('auth.validation.passwordRequirements')}
            </small>
          </div>

          {/* JELSZÓ MEGERŐSÍTÉS */}
          <div className="form-group">
            <label htmlFor="confirmPassword">
              {t('auth.confirmPassword')} *
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.confirmPassword')}
            />
            {fieldErrors.confirmPassword && (
              <span className="field-error">
                {fieldErrors.confirmPassword}
              </span>
            )}
          </div>

          {/* REGISTER GOMB */}
          <Button type="submit" variant="solid" fullWidth disabled={loading}>
            {loading ? t('auth.button.creating') : t('auth.button.register')}
          </Button>
        </form>

        {/* SSO GOMBÓK — csak ha be van kapcsolva */}
        {(ssoConfig.sso_google_enabled || ssoConfig.sso_facebook_enabled || ssoConfig.sso_apple_enabled) && (
          <>
            <div className="divider">
              <span>{t('auth.orContinueWith')}</span>
            </div>
            <div className="sso-buttons">
              {ssoConfig.sso_google_enabled   && <GoogleLoginButton />}
              {ssoConfig.sso_facebook_enabled && <FacebookLoginButton />}
              {ssoConfig.sso_apple_enabled    && <AppleLoginButton />}
            </div>
          </>
        )}

        {/* BEJELENTKEZÉS LINK */}
        <p className="auth-link">
          {t('auth.haveAccount')}{' '}
          <Link to="/login">
            {t('auth.loginHere')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;